/**
 * Os parsers da folha de pagamento em PDF: o relatório consolidado de
 * remunerações do eSocial, a "Relação geral dos líquidos" e o extrato mensal.
 *
 * Papel: aqui ficam `lerFolhaDoPdf`, que escolhe o parser pelo título do
 * relatório, os três parsers (`lerRelatorioConsolidado`, `lerRelacaoDeLiquidos`,
 * de `parseLiquidosPdfTexto`, l. 4225–4249, e `lerExtratoMensal`, de
 * `parseFolhaPdfTexto`, l. 4188–4219), as três peças que eles dividem —
 * `numeroDoPdf`, `nomeProprio` e `extrairLiquidoDoBloco` — e
 * `acharFuncionario`, que acha no PDF o funcionário de um cartão de Outros
 * Bancos.
 *
 * Como usar: os parsers recebem o texto de `extrairTextoDoPdf` e o mapa
 * CPF→nome de `GET financas/folha/nomes-por-cpf`. O nome gravado é o do mapa
 * quando o CPF do PDF está nele (comparado só pelos dígitos), e senão o nome do
 * PDF em nome próprio — sem o mapa, cada importação criaria um favorecido novo
 * para quem o PDF escreve em caixa alta. As expressões de `lerRelacaoDeLiquidos`
 * e `lerExtratoMensal` são as da origem e não deverão ser "melhoradas": a
 * conferência é contra os PDFs que a origem lia.
 */
import { MESES_POR_EXTENSO, type NomeDaFolha } from '@concierge/shared';

/** Um funcionário lido do PDF. O CPF vem como o PDF o escreve, ou vazio. */
export interface FuncionarioDoPdf {
  nome: string;
  cpf: string;
  liquido: number;
}

/** O que um parser da folha devolve. */
export interface FolhaDoPdf {
  funcionarios: FuncionarioDoPdf[];
  /** A competência em `AAAA-MM`, ou `null` quando o PDF não a traz. */
  competencia: string | null;
  competenciaDetectada: boolean;
  /** O total impresso no PDF, ou `null`. */
  totalPdf: number | null;
}

/** Um número monetário em formato brasileiro, como o PDF o escreve. */
const NUMERO_MONETARIO = /\d{1,3}(?:\.\d{3})*,\d{2}/g;

/** As palavras que ficam em minúsculas no meio de um nome. */
const MINUSCULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

/**
 * Lê um número do PDF, como o `parseBRLNum` da origem.
 *
 * @param texto - O número, como `1.234,56`.
 * @returns Os pontos tirados e a vírgula trocada; zero quando não é número.
 */
export function numeroDoPdf(texto: string): number {
  return Number(texto.replace(/\./g, '').replace(',', '.')) || 0;
}

/**
 * Escreve um nome de caixa alta como nome próprio.
 *
 * @param nome - O nome como o PDF o escreve.
 * @returns Cada palavra com a primeira letra maiúscula, menos "de", "da", "do",
 *   "das", "dos" e "e" quando não são a primeira; os espaços repetidos somem.
 */
export function nomeProprio(nome: string): string {
  return nome
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((palavra, indice) =>
      indice > 0 && MINUSCULAS.has(palavra)
        ? palavra
        : palavra.charAt(0).toUpperCase() + palavra.slice(1),
    )
    .join(' ');
}

/**
 * O líquido de um bloco do extrato mensal.
 *
 * @param bloco - O texto de um funcionário, do "Empr.:" dele ao do seguinte.
 * @returns O último número monetário da linha do **último** `ND:`; `null` sem
 *   `ND:` ou sem número nessa linha.
 */
export function extrairLiquidoDoBloco(bloco: string): number | null {
  const posicao = bloco.lastIndexOf('ND:');
  if (posicao === -1) return null;
  const linha = bloco.slice(posicao).split('\n')[0] ?? '';
  const numeros = linha.match(NUMERO_MONETARIO);
  const ultimo = numeros?.at(-1);
  return ultimo === undefined ? null : numeroDoPdf(ultimo);
}

/** O mapa CPF→nome pelos dígitos do CPF. */
function nomePorCpf(nomes: readonly NomeDaFolha[]): Map<string, string> {
  return new Map(nomes.map((par) => [par.cpf, par.nome]));
}

/** O nome a gravar: o do mapa pelo CPF, ou o do PDF em nome próprio. */
function nomeDoFuncionario(mapa: Map<string, string>, cpf: string, doPdf: string): string {
  return mapa.get(cpf.replace(/\D/g, '')) ?? nomeProprio(doPdf.replace(/\s+/g, ' ').trim());
}

/** Os funcionários sem o repetido pelo nome em maiúsculas; fica o primeiro. */
function semRepetidos(funcionarios: readonly FuncionarioDoPdf[]): FuncionarioDoPdf[] {
  const vistos = new Map<string, FuncionarioDoPdf>();
  for (const funcionario of funcionarios) {
    const chave = funcionario.nome.toUpperCase();
    if (!vistos.has(chave)) vistos.set(chave, funcionario);
  }
  return [...vistos.values()];
}

/** A competência `MM/AAAA` do texto, em `AAAA-MM`. */
function competenciaDoTexto(texto: string): string | null {
  const casamento = /Compet[êe]ncia[:\s]*?(0[1-9]|1[0-2])\s*\/\s*(20\d{2})/i.exec(texto);
  return casamento === null ? null : `${casamento[2] ?? ''}-${casamento[1] ?? ''}`;
}

/** O primeiro número depois de um rótulo de total, ou `null`. */
function totalDoTexto(texto: string, rotulo: RegExp): number | null {
  const casamento = rotulo.exec(texto);
  return casamento === null ? null : numeroDoPdf(casamento[1] ?? '');
}

/**
 * Lê a "Relação geral dos líquidos": uma linha por funcionário.
 *
 * @param texto - O texto do PDF.
 * @param nomes - O mapa CPF→nome.
 * @returns Cada linha `código NOME CPF valor` com líquido maior que zero, a
 *   competência e o "Total da Empresa".
 */
export function lerRelacaoDeLiquidos(
  texto: string,
  nomes: readonly NomeDaFolha[],
): FolhaDoPdf {
  const mapa = nomePorCpf(nomes);
  const funcionarios: FuncionarioDoPdf[] = [];
  for (const casamento of texto.matchAll(
    /^\s*(\d+)\s+([A-ZÀ-Ü][A-ZÀ-Ü\s]+?)\s+(\d{3}\.\d{3}\.\d{3}-\d{2})\s+([\d.,]+)\s*$/gm,
  )) {
    const cpf = casamento[3] ?? '';
    const liquido = numeroDoPdf(casamento[4] ?? '');
    if (!(liquido > 0)) continue;
    funcionarios.push({
      nome: nomeDoFuncionario(mapa, cpf, casamento[2] ?? ''),
      cpf,
      liquido,
    });
  }
  const competencia = competenciaDoTexto(texto);
  return {
    funcionarios: semRepetidos(funcionarios),
    competencia,
    competenciaDetectada: competencia !== null,
    totalPdf: totalDoTexto(texto, /Total da Empresa[:\s]*([\d.,]+)/i),
  };
}

/**
 * Lê o extrato mensal: um bloco por funcionário, de um "Empr.:" ao seguinte.
 *
 * @param texto - O texto do PDF.
 * @param nomes - O mapa CPF→nome.
 * @returns Cada bloco com líquido maior que zero, a competência e o "Líquido
 *   Geral".
 */
export function lerExtratoMensal(texto: string, nomes: readonly NomeDaFolha[]): FolhaDoPdf {
  const mapa = nomePorCpf(nomes);
  const inicios = [
    ...texto.matchAll(/Empr\.:\s*(\d+)\s+([A-ZÀ-Üa-zà-ü\s]+?)\s+Situa[çc][ãa]o:/g),
  ].map((casamento) => ({ posicao: casamento.index, nome: casamento[2] ?? '' }));

  const funcionarios: FuncionarioDoPdf[] = [];
  inicios.forEach((inicio, indice) => {
    const bloco = texto.slice(inicio.posicao, inicios[indice + 1]?.posicao ?? texto.length);
    const liquido = extrairLiquidoDoBloco(bloco);
    if (liquido === null || !(liquido > 0)) return;
    const cpf = /CPF:\s*([\d.\-/]+)/.exec(bloco)?.[1] ?? '';
    funcionarios.push({ nome: nomeDoFuncionario(mapa, cpf, inicio.nome), cpf, liquido });
  });

  const competencia = competenciaDoTexto(texto);
  return {
    funcionarios: semRepetidos(funcionarios),
    competencia,
    competenciaDetectada: competencia !== null,
    totalPdf: totalDoTexto(texto, /L[íi]quido\s*Geral[:\s]*([\d.,]+)/i),
  };
}

/** O texto em minúsculas e sem acento, para comparar o mês por extenso. */
function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** A competência do título "Remunerações - Outubro/2026", em `AAAA-MM`. */
function competenciaPorExtenso(texto: string): string | null {
  const casamento = /Remunera[çc][õo]es\s*-\s*(\p{L}+)\s*\/\s*(20\d{2})/iu.exec(texto);
  if (casamento === null) return null;
  const mes = semAcento(casamento[1] ?? '');
  const indice = MESES_POR_EXTENSO.findIndex((nome) => semAcento(nome) === mes);
  return indice === -1 ? null : `${casamento[2] ?? ''}-${String(indice + 1).padStart(2, '0')}`;
}

/** Os 11 dígitos do CPF com a máscara `000.000.000-00`. */
function cpfComMascara(cpf: string): string {
  return cpf.replace(/\D/g, '').replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

/**
 * Lê o relatório consolidado de remunerações do eSocial: um bloco por
 * trabalhador, de um "Trabalhador:" ao seguinte.
 *
 * O `CPF:` do cabeçalho "Empregador(a)" fica antes do primeiro "Trabalhador:" e
 * por isso nunca vira funcionário. O relatório não imprime total geral, e o
 * `totalPdf` volta `null` — a conferência mostra "—" no lugar dele.
 *
 * @param texto - O texto do PDF.
 * @param nomes - O mapa CPF→nome.
 * @returns Cada bloco com "TOTAL LÍQUIDO" maior que zero, com o CPF na máscara
 *   `000.000.000-00`, e a competência do mês por extenso do título.
 */
export function lerRelatorioConsolidado(
  texto: string,
  nomes: readonly NomeDaFolha[],
): FolhaDoPdf {
  const mapa = nomePorCpf(nomes);
  const inicios = [...texto.matchAll(/Trabalhador:\s*([\d.-]{11,14})\s*-\s*(.+?)\s*$/gm)].map(
    (casamento) => ({
      posicao: casamento.index,
      cpf: cpfComMascara(casamento[1] ?? ''),
      nome: casamento[2] ?? '',
    }),
  );

  const funcionarios: FuncionarioDoPdf[] = [];
  inicios.forEach((inicio, indice) => {
    const bloco = texto.slice(inicio.posicao, inicios[indice + 1]?.posicao ?? texto.length);
    const valor = /TOTAL\s+L[ÍI]QUIDO:\s*R\$\s*([\d.,]+)/i.exec(bloco)?.[1];
    const liquido = valor === undefined ? 0 : numeroDoPdf(valor);
    if (!(liquido > 0)) return;
    funcionarios.push({
      nome: nomeDoFuncionario(mapa, inicio.cpf, inicio.nome),
      cpf: inicio.cpf,
      liquido,
    });
  });

  const competencia = competenciaPorExtenso(texto);
  return {
    funcionarios: semRepetidos(funcionarios),
    competencia,
    competenciaDetectada: competencia !== null,
    totalPdf: null,
  };
}

/**
 * Lê uma folha em PDF, escolhendo o parser pelo título do relatório.
 *
 * @param texto - O texto do PDF.
 * @param nomes - O mapa CPF→nome.
 * @returns O que `lerRelatorioConsolidado` devolve quando o texto tem
 *   "Relatório Consolidado de Remunerações", o que `lerRelacaoDeLiquidos`
 *   devolve quando tem "Relação geral dos líquidos" (os dois com ou sem acento,
 *   em qualquer caixa), e o que `lerExtratoMensal` devolve nos outros casos.
 */
export function lerFolhaDoPdf(texto: string, nomes: readonly NomeDaFolha[]): FolhaDoPdf {
  if (/RELAT[ÓO]RIO CONSOLIDADO DE REMUNERA[ÇC][ÕO]ES/i.test(texto)) {
    return lerRelatorioConsolidado(texto, nomes);
  }
  return /RELA[ÇC][ÃA]O GERAL DOS L[ÍI]QUIDOS/i.test(texto)
    ? lerRelacaoDeLiquidos(texto, nomes)
    : lerExtratoMensal(texto, nomes);
}

/**
 * Acha no PDF o funcionário de um cartão de Outros Bancos.
 *
 * Tenta, nesta ordem: o CPF do cadastro, comparando só os dígitos; o nome em
 * maiúsculas; e, se o PDF tiver um funcionário só, ele.
 *
 * @param funcionarios - Os funcionários que o parser leu.
 * @param favorecido - O nome e o CPF do cadastro do cartão.
 * @returns O funcionário, ou `null` quando nenhum dos três passos acha.
 */
export function acharFuncionario(
  funcionarios: readonly FuncionarioDoPdf[],
  favorecido: { nome: string; cpf: string | null },
): FuncionarioDoPdf | null {
  const cpf = (favorecido.cpf ?? '').replace(/\D/g, '');
  const peloCpf =
    cpf === '' ? undefined : funcionarios.find((f) => f.cpf.replace(/\D/g, '') === cpf);
  if (peloCpf !== undefined) return peloCpf;
  const peloNome = funcionarios.find(
    (f) => f.nome.toUpperCase() === favorecido.nome.toUpperCase(),
  );
  if (peloNome !== undefined) return peloNome;
  const [unico] = funcionarios;
  return funcionarios.length === 1 && unico !== undefined ? unico : null;
}
